import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from './cn';

function Select({ value, onValueChange, children }) {
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (val, label) => {
      if (onValueChange) onValueChange(val);
      if (label) setSelectedLabel(label);
      setOpen(false);
    },
    [onValueChange]
  );

  return (
    <div ref={containerRef} className="relative">
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, {
          _selectOpen: open,
          _setSelectOpen: setOpen,
          _selectValue: value,
          _selectLabel: selectedLabel,
          _onSelect: handleSelect,
        });
      })}
    </div>
  );
}

const SelectGroup = ({ children }) => children;

function SelectValue({ placeholder, _selectValue, _selectLabel }) {
  return (
    <span className={cn('block truncate', !_selectLabel && 'text-succession-text-secondary')}>
      {_selectLabel || placeholder}
    </span>
  );
}

const SelectTrigger = React.forwardRef(
  ({ className, children, id, _selectOpen, _setSelectOpen, _selectValue, _selectLabel, _onSelect, ...props }, ref) => {
    return (
      <button
        ref={ref}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={_selectOpen}
        onClick={() => _setSelectOpen && _setSelectOpen(!_selectOpen)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-succession-border bg-succession-bg-primary px-3 py-2 text-sm text-succession-text-primary placeholder:text-succession-text-secondary focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-300',
          className
        )}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          if (child.type === SelectValue || child.type?.displayName === 'SelectValue') {
            return React.cloneElement(child, { _selectValue, _selectLabel });
          }
          return child;
        })}
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
      </button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

function SelectContent({ children, className, _selectOpen, _selectValue, _onSelect, _setSelectOpen }) {
  if (!_selectOpen) return null;

  return (
    <div
      className={cn(
        'absolute z-50 mt-1 max-h-96 w-full min-w-[8rem] overflow-auto rounded-md border border-succession-border bg-succession-bg-primary text-succession-text-primary shadow-succession p-1',
        className
      )}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child, { _selectValue, _onSelect });
      })}
    </div>
  );
}

function SelectItem({ className, children, value, textValue, _selectValue, _onSelect, ...props }) {
  const isSelected = _selectValue === value;

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-succession-bg-secondary focus:bg-succession-bg-secondary',
        isSelected && 'bg-succession-bg-secondary',
        className
      )}
      onClick={() => _onSelect && _onSelect(value, textValue)}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {isSelected && <Check className="h-4 w-4" />}
      </span>
      <span>{children}</span>
    </div>
  );
}

const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
